import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { Link, useSearchParams } from 'react-router-dom'
import { BadgeCheck, ClipboardList, FileSpreadsheet, FlaskConical, Plus, Trash2 } from 'lucide-react'
import {
  useCapabilityOptions,
  useMethodDefinitions,
  useResourceMethods,
  useResourceProcedures,
} from '@/hooks/useResourceCapabilities'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import { PROCEDURE_TYPE_LABELS, type ProcedureType } from '@/types/cgt'
import {
  EMPTY_CELL,
  formatLastUsed,
  formatProficiency,
  parsePreference,
  parseProficiency,
} from '@/lib/resourceCapabilities'
import type {
  CapabilityOptions,
  ResourceMethodRow,
  ResourceProcedureRow,
} from '@/api/resourceCapabilitiesApi'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { MethodDefinitionGrid } from './MethodDefinitionGrid'
import { QualificationImportDialog } from './QualificationImportDialog'

/**
 * Configure → Method: the resource capability matrix.
 *
 * Three views of master data:
 *
 *   Procedures     — which procedures each person can perform.
 *   Methods        — the methods themselves, each a SET OF PROCESSES.
 *   Qualifications — who has been given which method, and so which processes
 *                    they can perform.
 *
 * The middle one is what makes the other two mean anything. A method used to be
 * a LABEL in an option set: it named a thing without ever saying what it
 * covered, and the steps it was meant to cover were to be inferred from a
 * `method` string in each process step's free-form attributes, which nothing
 * ever wrote.
 *
 * Nothing GATES on this yet — narrowing the process-step assignee picker to
 * qualified people is a later ticket, and this is the data it will read. What
 * does read it already is Last Date Used, which joins a method's steps to
 * completed executions.
 *
 * "Resource" is the label the mockup uses for a person. There is no Resource
 * entity in this app, so every row binds to a user id from the tenant roster.
 *
 * Every grid ADDs by putting an unsaved row in the table and assigning in it.
 * There is deliberately no strip above the table asking for the pair before a
 * row exists: one place to assign, the same controls whether the row is new or
 * being changed, and no second code path that can drift from the first.
 */
export function MethodSettings() {
  const { can } = usePermission()
  // Gated on the permission whose ROUTES these controls call — the write routes
  // are tenant-scoped, so this resolves against the caller's role in the
  // selected tenant rather than a global baseline.
  const canManage = can(PERMISSIONS.resourceCapabilitiesManage)
  const options = useCapabilityOptions()

  /**
   * Which grid is on screen, in the URL beside Configure's own `?tab=method`.
   *
   * Same reason the tab itself is a param: a reload, a Back, or a link shared
   * with someone lands on the view that was being talked about. Unknown values
   * fall back to Procedures rather than rendering nothing, which is how
   * ConfigurePage treats its own `?tab=`.
   */
  const [params, setParams] = useSearchParams()
  const requested = params.get('view')
  const view: CapabilityView = VIEWS.some(v => v.value === requested)
    ? (requested as CapabilityView)
    : 'procedures'

  function setView(next: CapabilityView) {
    const sp = new URLSearchParams(params)
    sp.set('view', next)
    setParams(sp, { replace: true })
  }

  // Counted here so the switcher can say how much is in the view you are NOT
  // looking at — the reason to open it. Both are TanStack queries keyed per
  // tenant, so these share the fetch the grids below already make.
  const procedureCount = useResourceProcedures().list.data?.length
  const qualificationCount = useResourceMethods().list.data?.length
  const methodCount = useMethodDefinitions().list.data?.length
  const [importOpen, setImportOpen] = useState(false)
  const qc = useQueryClient()
  const countOf = (v: CapabilityView) =>
    v === 'procedures' ? procedureCount : v === 'methods' ? methodCount : qualificationCount

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-start justify-between gap-4'>
        <div>
          <h2 className='text-sm font-semibold text-foreground'>Resource capabilities</h2>
          <p className='mt-0.5 text-sm text-muted-foreground'>
            {/* The tab is called "Method" and one view is about procedures, so it
                says who can do what. The views name themselves from there. */}
            Who can perform what, and the methods that define it.
          </p>
        </div>
        {/* Bulk assignment sits beside the heading, not inside one grid: the
            workbook carries BOTH matrices, so it belongs to neither view. */}
        {canManage && (
          <Button variant='outline' size='sm' onClick={() => setImportOpen(true)}>
            <FileSpreadsheet className='mr-1.5 h-3.5 w-3.5' />
            Import from Excel
          </Button>
        )}
      </div>

      <QualificationImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        // The whole matrix, not one grid: a file can change both, and the
        // counts in the view switcher are read from these lists too.
        onImported={() => void qc.invalidateQueries({ queryKey: ['resource-capabilities'] })}
      />

      {/*
        A segmented control, not a nested <Tabs>. Configure is already a tablist,
        and a second one inside it stacks two strips of tabs and adds a second
        `role="tabpanel"` to the page — which is exactly what the Integrations
        Receive/Send switch avoided, for the sake of the unscoped
        `getByRole('tabpanel')` locators its own specs use.
      */}
      <div
        role='group'
        aria-label='Capability view'
        className='inline-flex items-center gap-1 self-start rounded-lg border border-border/60 bg-muted/40 p-1'
      >
        {VIEWS.map(({ value, label, icon: Icon }) => {
          const active = view === value
          const count = countOf(value)
          return (
            <button
              key={value}
              type='button'
              aria-pressed={active}
              onClick={() => setView(value)}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                active
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className='h-3.5 w-3.5' />
              {label}
              {/* Absent until the list resolves — a zero shown while loading
                  reads as "nothing here", which is a different claim. */}
              {count !== undefined && (
                <span
                  className={cn(
                    'rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums',
                    active ? 'bg-muted text-foreground' : 'bg-background/60',
                  )}
                >
                  {count}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {view === 'procedures' ? (
        <ProcedureGrid canManage={canManage} options={options.data} loading={options.isLoading} />
      ) : view === 'methods' ? (
        <MethodDefinitionGrid
          canManage={canManage}
          options={options.data}
          loading={options.isLoading}
        />
      ) : (
        <MethodGrid canManage={canManage} options={options.data} loading={options.isLoading} />
      )}
    </div>
  )
}

/**
 * The three things on this tab, and what the switcher calls them.
 *
 * `methods` DEFINES what a method is — a set of processes. `qualifications` is
 * who has been given one. They were one screen while a method was just a label,
 * because there was nothing to define.
 */
type CapabilityView = 'procedures' | 'methods' | 'qualifications'

const VIEWS: { value: CapabilityView; label: string; icon: React.ElementType }[] = [
  { value: 'procedures', label: 'Procedures', icon: ClipboardList },
  { value: 'methods', label: 'Methods', icon: FlaskConical },
  { value: 'qualifications', label: 'Qualifications', icon: BadgeCheck },
]

/**
 * The unsaved rows.
 *
 * Add renders one of these in edit mode: the row components already build their
 * drafts from the row they are given, so a blank one produces empty controls
 * with no second code path to keep in step. `id` is never sent — the create
 * takes only what the row's Save hands it.
 */
const DRAFT_PROCEDURE_ROW: ResourceProcedureRow = {
  id: '',
  userId: '',
  procedureFamilyId: '',
  resourceName: '',
  procedureName: null,
  procedureType: null,
}

/** Preference 1 and 100% are the defaults the old add strip carried. */
const DRAFT_METHOD_ROW: ResourceMethodRow = {
  id: '',
  userId: '',
  resourceName: '',
  methodId: '',
  methodName: '',
  preference: 1,
  proficiency: 100,
  lastUsedAt: null,
}

/** The procedure's kind, or a dash — a retired template has none to read. */
function procedureTypeLabel(type: ProcedureType | null | undefined): string {
  return type ? PROCEDURE_TYPE_LABELS[type] : EMPTY_CELL
}

// ─── Resource × Procedure ────────────────────────────────────────────────────

// Per-user column layouts for the two grids on this screen, kept as separate
// tables: they share a Resource Name column but nothing else, so one layout
// would describe neither. Each grid's row component calls the hook with the
// same id as its header, so the two stay in step.
// See src/components/table/index.ts for the pattern.
const PROCEDURE_QUAL_TABLE_ID = 'method-procedure-qualifications'

const PROCEDURE_QUAL_COLUMNS: TableColumnDef[] = [
  { id: 'resourceName', header: 'Resource Name', locked: true, sortable: true },
  { id: 'procedure', header: 'Procedure', locked: true, sortable: true },
  { id: 'procedureType', header: 'Procedure Type', sortable: true },
  { id: 'actions', header: 'Actions', locked: true },
]

// Column sorting (z8nrz7e4h6). A missing procedure or type sorts last rather
// than as its dash.
const PROCEDURE_QUAL_SORT: SortAccessors<ResourceProcedureRow> = {
  resourceName: (r) => r.resourceName,
  procedure: (r) => r.procedureName,
  procedureType: (r) => (r.procedureType ? PROCEDURE_TYPE_LABELS[r.procedureType] : null),
}

/* Percentages kept rather than pixel defaults — they keep filling the panel. */
const PROCEDURE_QUAL_HEAD_CLASSES: Record<string, string> = {
  resourceName: 'w-[30%]',
  procedure: 'w-[30%]',
  procedureType: 'w-[20%]',
  actions: 'w-[20%]',
}

const METHOD_QUAL_TABLE_ID = 'method-qualifications'

const METHOD_QUAL_COLUMNS: TableColumnDef[] = [
  { id: 'resourceName', header: 'Resource Name', locked: true, sortable: true },
  { id: 'method', header: 'Method', locked: true, sortable: true },
  { id: 'preference', header: 'Preference', sortable: true },
  { id: 'proficiency', header: 'Proficiency', sortable: true },
  { id: 'lastUsed', header: 'Last Date Used', sortable: true },
  { id: 'actions', header: 'Actions', locked: true },
]

// Column sorting (z8nrz7e4h6): the raw numbers and timestamp, not their display.
const METHOD_QUAL_SORT: SortAccessors<ResourceMethodRow> = {
  resourceName: (r) => r.resourceName,
  method: (r) => r.methodName,
  preference: (r) => r.preference,
  proficiency: (r) => r.proficiency,
  lastUsed: (r) => r.lastUsedAt,
}

const METHOD_QUAL_HEAD_CLASSES: Record<string, string> = {
  resourceName: 'w-[24%]',
  method: 'w-[22%]',
  preference: 'w-[12%] text-right',
  proficiency: 'w-[14%] text-right',
  lastUsed: 'w-[16%]',
  actions: 'w-[12%]',
}

function ProcedureGrid({
  canManage,
  options,
  loading,
}: {
  canManage: boolean
  options: CapabilityOptions | undefined
  loading: boolean
}) {
  const cols = useTableColumns(PROCEDURE_QUAL_TABLE_ID, PROCEDURE_QUAL_COLUMNS)
  const { list, create, update, remove } = useResourceProcedures()
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  /**
   * Whether the unsaved new row is on screen.
   *
   * Add puts a row in the table and the assigning happens IN it — there is no
   * separate strip above the table asking for a resource and a procedure before
   * a row exists. One place to assign, whether the row is new or being changed,
   * and the same controls in both.
   */
  const [adding, setAdding] = useState(false)

  const resources = options?.resources ?? []
  const procedures = options?.procedures ?? []
  const rows = useSortedRows(list.data, cols.sort, PROCEDURE_QUAL_SORT)

  const resourceOptions = resources.map((r) => ({ value: r.id, label: r.name }))
  // Carries the type, so the row can name the kind of work as soon as a
  // procedure is picked rather than only after the save comes back.
  const procedureOptions = procedures.map((p) => ({
    value: p.familyId,
    label: p.name,
    type: p.type ?? null,
  }))

  return (
    <Card>
      <CardContent className='pt-6'>
        <h3 className='text-sm font-semibold text-foreground'>Procedures</h3>
        <p className='mb-4 mt-0.5 text-sm text-muted-foreground'>
          {/* The count lives on the switcher above, where it is the reason to
              open a view rather than a number beside the thing it counts. */}
          Which procedures each resource can perform.
        </p>

        {canManage && (
          <div className='mb-4'>
            <Button
              onClick={() => {
                setError(null)
                setEditingId(null)
                setAdding(true)
              }}
              disabled={adding}
              className='gap-1.5'
            >
              <Plus className='h-4 w-4' />
              Add
            </Button>
          </div>
        )}
        {error && <p className='mb-3 text-sm font-medium text-red-600'>{error}</p>}

        <div className='overflow-x-auto rounded-lg border border-gray-200'>
          {/* Procedure Type is which KIND of work the qualification is for: two
              templates can share a name across types, and the grid offers every
              type rather than transplants alone. */}
          <Table>
            <TableHeader>
              <TableRow>
                {cols.visible.map(({ def }) => (
                  <ResizableTableHead
                    key={def.id}
                    columnId={def.id}
                    columns={cols}
                    label={def.header}
                    className={PROCEDURE_QUAL_HEAD_CLASSES[def.id]}
                    resizable={def.id !== 'actions'}
                  >
                    {def.header}
                  </ResizableTableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* The unsaved row sits at the top, where the strip used to be, so
                  Add still puts the thing to fill in where the eye already is. */}
              {adding && (
                <ProcedureRow
                  row={DRAFT_PROCEDURE_ROW}
                  canManage={canManage}
                  editing
                  confirming={false}
                  resourceOptions={resourceOptions}
                  procedureOptions={procedureOptions}
                  saveLabel='Add'
                  onEdit={() => {}}
                  onCancel={() => setAdding(false)}
                  onSave={async (payload) => {
                    setError(null)
                    try {
                      await create.mutateAsync(payload)
                      setAdding(false)
                    } catch (e) {
                      setError(e instanceof Error ? e.message : 'Could not add that row.')
                    }
                  }}
                  onAskRemove={() => {}}
                  onCancelRemove={() => {}}
                  onRemove={async () => {}}
                />
              )}
              {loading || list.isLoading ? (
                <EmptyRow colSpan={cols.visible.length}>Loading…</EmptyRow>
              ) : rows.length === 0 ? (
                // Not shown while the new row is up: the table is not empty then,
                // and "No procedures yet" under a row being filled in reads as a
                // failure to save it.
                adding ? null : <EmptyRow colSpan={cols.visible.length}>No procedures yet.</EmptyRow>
              ) : (
                rows.map((row) => (
                  <ProcedureRow
                    key={row.id}
                    row={row}
                    canManage={canManage}
                    editing={editingId === row.id}
                    confirming={confirmingId === row.id}
                    resourceOptions={resourceOptions}
                    procedureOptions={procedureOptions}
                    onEdit={() => {
                      setEditingId(row.id)
                      setConfirmingId(null)
                    }}
                    onCancel={() => setEditingId(null)}
                    onSave={async (payload) => {
                      setError(null)
                      try {
                        await update.mutateAsync({ id: row.id, payload })
                        setEditingId(null)
                      } catch (e) {
                        setError(e instanceof Error ? e.message : 'Could not save that row.')
                      }
                    }}
                    onAskRemove={() => setConfirmingId(row.id)}
                    onCancelRemove={() => setConfirmingId(null)}
                    onRemove={async () => {
                      setError(null)
                      try {
                        await remove.mutateAsync(row.id)
                        setConfirmingId(null)
                      } catch (e) {
                        setError(e instanceof Error ? e.message : 'Could not remove that row.')
                      }
                    }}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

function ProcedureRow({
  row,
  canManage,
  editing,
  confirming,
  resourceOptions,
  procedureOptions,
  saveLabel = 'Save',
  onEdit,
  onCancel,
  onSave,
  onAskRemove,
  onCancelRemove,
  onRemove,
}: {
  row: ResourceProcedureRow
  canManage: boolean
  editing: boolean
  confirming: boolean
  resourceOptions: { value: string; label: string }[]
  procedureOptions: { value: string; label: string; type: ProcedureType | null }[]
  /** 'Add' on the unsaved row, 'Save' when changing one that exists. */
  saveLabel?: string
  onEdit: () => void
  onCancel: () => void
  onSave: (payload: { userId: string; procedureFamilyId: string }) => Promise<void>
  onAskRemove: () => void
  onCancelRemove: () => void
  onRemove: () => Promise<void>
}) {
  // Same table id as ProcedureGrid's header, so the two stay in step.
  const cols = useTableColumns(PROCEDURE_QUAL_TABLE_ID, PROCEDURE_QUAL_COLUMNS)
  const [draftUser, setDraftUser] = useState(row.userId)
  const [draftFamily, setDraftFamily] = useState(row.procedureFamilyId)

  if (editing) {
    // The type follows the chosen procedure, so the row names the kind of work
    // while it is still being filled in.
    const draftType = procedureOptions.find((p) => p.value === draftFamily)?.type ?? null
    return (
      <TableRow>
        <ColumnCells
          columns={cols}
          cells={{
            resourceName: (
              <TableCell>
                <SearchableSelect
                  value={draftUser}
                  onChange={setDraftUser}
                  options={resourceOptions}
                  placeholder={
                    resourceOptions.length ? 'Select a resource…' : 'No members in this tenant'
                  }
                  disabled={!resourceOptions.length}
                  emptyMessage='No matching people.'
                />
              </TableCell>
            ),
            procedure: (
              <TableCell>
                <SearchableSelect
                  value={draftFamily}
                  onChange={setDraftFamily}
                  options={procedureOptions}
                  placeholder={procedureOptions.length ? 'Select a procedure…' : 'No templates yet'}
                  disabled={!procedureOptions.length}
                  emptyMessage='No matching templates.'
                />
              </TableCell>
            ),
            procedureType: <TableCell className='text-muted-foreground'>{procedureTypeLabel(draftType)}</TableCell>,
            actions: (
              <TableCell className='text-right'>
                <div className='flex justify-end gap-2'>
                  <Button variant='outline' size='sm' onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button
                    size='sm'
                    // Half a row is not a row. Disabled rather than answering with
                    // "Pick a resource and a procedure" after the click — the button
                    // says what is missing by staying dead.
                    disabled={!draftUser || !draftFamily}
                    onClick={() => void onSave({ userId: draftUser, procedureFamilyId: draftFamily })}
                  >
                    {saveLabel}
                  </Button>
                </div>
              </TableCell>
            ),
          }}
        />
      </TableRow>
    )
  }

  return (
    <TableRow>
      <ColumnCells
        columns={cols}
        cells={{
          resourceName: <TableCell className='font-medium'>{row.resourceName}</TableCell>,
          procedure: (
            <TableCell>
              {row.procedureName ?? (
                // The template was retired or deleted. The row is stale rather than
                // broken, and saying so is what lets someone clean it up.
                <span className='text-muted-foreground'>
                  {EMPTY_CELL} <span className='text-xs'>(template no longer active)</span>
                </span>
              )}
            </TableCell>
          ),
          procedureType: (
            <TableCell className={cn(!row.procedureType && 'text-muted-foreground')}>
              {procedureTypeLabel(row.procedureType)}
            </TableCell>
          ),
          actions: (
            <TableCell className='text-right'>
              <RowActions
                canManage={canManage}
                confirming={confirming}
                onEdit={onEdit}
                onAskRemove={onAskRemove}
                onCancelRemove={onCancelRemove}
                onRemove={onRemove}
              />
            </TableCell>
          ),
        }}
      />
    </TableRow>
  )
}

// ─── Resource × Method ───────────────────────────────────────────────────────

function MethodGrid({
  canManage,
  options,
  loading,
}: {
  canManage: boolean
  options: CapabilityOptions | undefined
  loading: boolean
}) {
  const cols = useTableColumns(METHOD_QUAL_TABLE_ID, METHOD_QUAL_COLUMNS)
  const { list, create, update, remove } = useResourceMethods()
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  /** The unsaved new row — see the note in ProcedureGrid. */
  const [adding, setAdding] = useState(false)

  const resources = options?.resources ?? []
  const methods = options?.methods ?? []
  const rows = useSortedRows(list.data, cols.sort, METHOD_QUAL_SORT)
  // Nothing to qualify anyone IN. The fix is defining a method, which is the
  // view next door — so this says that rather than offering an empty dropdown.
  const noMethods = !loading && methods.length === 0

  const resourceOptions = resources.map((r) => ({ value: r.id, label: r.name }))
  /**
   * The step count rides in the label, because a method covering no processes
   * qualifies nobody for anything — and that is invisible from its name alone.
   */
  const methodOptions = methods.map((m) => ({
    value: m.id,
    label: m.stepCount === 0 ? `${m.name} (no processes yet)` : `${m.name} · ${m.stepCount} processes`,
  }))

  return (
    <Card>
      <CardContent className='pt-6'>
        <h3 className='text-sm font-semibold text-foreground'>Qualifications</h3>
        <p className='mb-4 mt-0.5 text-sm text-muted-foreground'>
          Which methods each resource is qualified in. Lower preference wins.
        </p>

        {noMethods ? (
          // Deliberately not an empty dropdown: there is nothing to qualify
          // anyone in yet, and the fix is one view away.
          <div className='rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900'>
            No methods yet. Define one under{' '}
            <Link to='/configure?tab=method&view=methods' className='font-medium underline'>
              Methods
            </Link>{' '}
            first.
          </div>
        ) : (
          <>
            {canManage && (
              <div className='mb-4'>
                <Button
                  onClick={() => {
                    setError(null)
                    setEditingId(null)
                    setAdding(true)
                  }}
                  disabled={adding}
                  className='gap-1.5'
                >
                  <Plus className='h-4 w-4' />
                  Add
                </Button>
              </div>
            )}
            {error && <p className='mb-3 text-sm font-medium text-red-600'>{error}</p>}

            <div className='overflow-x-auto rounded-lg border border-gray-200'>
              <Table>
                <TableHeader>
                  <TableRow>
                    {cols.visible.map(({ def }) => (
                      <ResizableTableHead
                        key={def.id}
                        columnId={def.id}
                        columns={cols}
                        label={def.header}
                        className={METHOD_QUAL_HEAD_CLASSES[def.id]}
                        resizable={def.id !== 'actions'}
                      >
                        {def.header}
                      </ResizableTableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {adding && (
                    <MethodRow
                      row={DRAFT_METHOD_ROW}
                      canManage={canManage}
                      editing
                      confirming={false}
                      resourceOptions={resourceOptions}
                      methodOptions={methodOptions}
                      saveLabel='Add'
                      onEdit={() => {}}
                      onCancel={() => setAdding(false)}
                      onSave={async (payload) => {
                        setError(null)
                        try {
                          await create.mutateAsync(payload)
                          setAdding(false)
                        } catch (e) {
                          setError(e instanceof Error ? e.message : 'Could not add that row.')
                        }
                      }}
                      onInvalid={setError}
                      onAskRemove={() => {}}
                      onCancelRemove={() => {}}
                      onRemove={async () => {}}
                    />
                  )}
                  {loading || list.isLoading ? (
                    <EmptyRow colSpan={cols.visible.length}>Loading…</EmptyRow>
                  ) : rows.length === 0 ? (
                    adding ? null : <EmptyRow colSpan={cols.visible.length}>No qualifications yet.</EmptyRow>
                  ) : (
                    rows.map((row) => (
                      <MethodRow
                        key={row.id}
                        row={row}
                        canManage={canManage}
                        editing={editingId === row.id}
                        confirming={confirmingId === row.id}
                        resourceOptions={resourceOptions}
                        methodOptions={methodOptions}
                        onEdit={() => {
                          setEditingId(row.id)
                          setConfirmingId(null)
                        }}
                        onCancel={() => setEditingId(null)}
                        onSave={async (payload) => {
                          setError(null)
                          try {
                            await update.mutateAsync({ id: row.id, payload })
                            setEditingId(null)
                          } catch (e) {
                            setError(e instanceof Error ? e.message : 'Could not save that row.')
                          }
                        }}
                        onInvalid={setError}
                        onAskRemove={() => setConfirmingId(row.id)}
                        onCancelRemove={() => setConfirmingId(null)}
                        onRemove={async () => {
                          setError(null)
                          try {
                            await remove.mutateAsync(row.id)
                            setConfirmingId(null)
                          } catch (e) {
                            setError(e instanceof Error ? e.message : 'Could not remove that row.')
                          }
                        }}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <p className='mt-2 text-xs text-muted-foreground'>
              Last Date Used is read-only — the method's newest completed process.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function MethodRow({
  row,
  canManage,
  editing,
  confirming,
  resourceOptions,
  methodOptions,
  saveLabel = 'Save',
  onEdit,
  onCancel,
  onSave,
  onInvalid,
  onAskRemove,
  onCancelRemove,
  onRemove,
}: {
  row: ResourceMethodRow
  canManage: boolean
  editing: boolean
  confirming: boolean
  resourceOptions: { value: string; label: string }[]
  methodOptions: { value: string; label: string }[]
  /** 'Add' on the unsaved row, 'Save' when changing one that exists. */
  saveLabel?: string
  onEdit: () => void
  onCancel: () => void
  onSave: (payload: {
    userId: string
    methodId: string
    preference: number
    proficiency: number
  }) => Promise<void>
  onInvalid: (message: string) => void
  onAskRemove: () => void
  onCancelRemove: () => void
  onRemove: () => Promise<void>
}) {
  // Same table id as MethodGrid's header, so the two stay in step.
  const cols = useTableColumns(METHOD_QUAL_TABLE_ID, METHOD_QUAL_COLUMNS)
  const [draftUser, setDraftUser] = useState(row.userId)
  const [draftMethod, setDraftMethod] = useState(row.methodId)
  const [draftPreference, setDraftPreference] = useState(String(row.preference))
  const [draftProficiency, setDraftProficiency] = useState(String(row.proficiency))

  function save() {
    const pref = parsePreference(draftPreference)
    if (!pref.ok) {
      onInvalid(pref.error)
      return
    }
    const prof = parseProficiency(draftProficiency)
    if (!prof.ok) {
      onInvalid(prof.error)
      return
    }
    void onSave({
      userId: draftUser,
      methodId: draftMethod,
      preference: pref.value,
      proficiency: prof.value,
    })
  }

  if (editing) {
    return (
      <TableRow>
        <ColumnCells
          columns={cols}
          cells={{
            resourceName: (
              <TableCell>
                <SearchableSelect
                  value={draftUser}
                  onChange={setDraftUser}
                  options={resourceOptions}
                  placeholder='Select a resource…'
                />
              </TableCell>
            ),
            method: (
              <TableCell>
                <SearchableSelect
                  value={draftMethod}
                  onChange={setDraftMethod}
                  options={methodOptions}
                  placeholder='Select a method…'
                />
              </TableCell>
            ),
            preference: (
              <TableCell>
                <Input
                  type='number'
                  min={1}
                  step={1}
                  value={draftPreference}
                  onChange={(e) => setDraftPreference(e.target.value)}
                  className='text-right'
                />
              </TableCell>
            ),
            proficiency: (
              <TableCell>
                <Input
                  type='number'
                  min={0}
                  max={100}
                  value={draftProficiency}
                  onChange={(e) => setDraftProficiency(e.target.value)}
                  className='text-right'
                />
              </TableCell>
            ),
            lastUsed: <TableCell className='text-muted-foreground'>{formatLastUsed(row.lastUsedAt)}</TableCell>,
            actions: (
              <TableCell className='text-right'>
                <div className='flex justify-end gap-2'>
                  <Button variant='outline' size='sm' onClick={onCancel}>
                    Cancel
                  </Button>
                  {/* Preference and proficiency are validated on click (a number can
                      be wrong, not just missing); the two that can only be missing
                      keep the button dead instead. */}
                  <Button size='sm' disabled={!draftUser || !draftMethod} onClick={save}>
                    {saveLabel}
                  </Button>
                </div>
              </TableCell>
            ),
          }}
        />
      </TableRow>
    )
  }

  return (
    <TableRow>
      <ColumnCells
        columns={cols}
        cells={{
          resourceName: <TableCell className='font-medium'>{row.resourceName}</TableCell>,
          method: <TableCell>{row.methodName}</TableCell>,
          preference: <TableCell className='text-right tabular-nums'>{row.preference}</TableCell>,
          proficiency: (
            <TableCell className='text-right tabular-nums'>
              {formatProficiency(row.proficiency)}
            </TableCell>
          ),
          lastUsed: (
            <TableCell className={cn('tabular-nums', !row.lastUsedAt && 'text-muted-foreground')}>
              {formatLastUsed(row.lastUsedAt)}
            </TableCell>
          ),
          actions: (
            <TableCell className='text-right'>
              <RowActions
                canManage={canManage}
                confirming={confirming}
                onEdit={onEdit}
                onAskRemove={onAskRemove}
                onCancelRemove={onCancelRemove}
                onRemove={onRemove}
              />
            </TableCell>
          ),
        }}
      />
    </TableRow>
  )
}

// ─── Shared bits ─────────────────────────────────────────────────────────────

/**
 * Edit / Remove for one row, with the confirmation happening IN the row rather
 * than in a dialog — the thing being deleted is one line, and a modal asking
 * about it hides the line it is asking about.
 */
function RowActions({
  canManage,
  confirming,
  onEdit,
  onAskRemove,
  onCancelRemove,
  onRemove,
}: {
  canManage: boolean
  confirming: boolean
  onEdit: () => void
  onAskRemove: () => void
  onCancelRemove: () => void
  onRemove: () => Promise<void>
}) {
  if (!canManage) return null
  if (confirming) {
    return (
      <div className='flex items-center justify-end gap-2'>
        <span className='text-xs text-muted-foreground'>Remove?</span>
        <Button variant='outline' size='sm' onClick={onCancelRemove}>
          No
        </Button>
        <Button
          size='sm'
          className='bg-red-600 hover:bg-red-700'
          onClick={() => void onRemove()}
        >
          Yes
        </Button>
      </div>
    )
  }
  return (
    <div className='flex items-center justify-end gap-1'>
      <Button variant='ghost' size='sm' onClick={onEdit}>
        Edit
      </Button>
      <Button
        variant='ghost'
        size='sm'
        onClick={onAskRemove}
        className='text-red-600 hover:bg-red-50 hover:text-red-700'
        aria-label='Remove row'
      >
        <Trash2 className='h-4 w-4' />
      </Button>
    </div>
  )
}

function EmptyRow({ colSpan, children }: { colSpan: number; children: React.ReactNode }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className='py-10 text-center text-sm text-muted-foreground'>
        {children}
      </TableCell>
    </TableRow>
  )
}
